import React, { useEffect, Suspense, useState } from 'react';
import CategoryRail from './CategoryRail';
import LoadingSpinner from './common/LoadingSpinner';
import ErrorBoundary from './common/ErrorBoundary';
import spotlightService from '../services/SpotlightService';
import { cacheService } from '../services/CacheService';
import { useNavigate } from 'react-router-dom';

const MoviesPage = ({ onCategorySelect, onMovieSelect, onSeriesSelect }) => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchMoviesData = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await spotlightService.getMoviesHomePage();

      // Process homepage data to extract categories with their channels
      const processedCategories =
        data.movies?.map(item => ({
          _id: item.category._id,
          name: item.category.name,
          display_name: item.category.name,
          channels: item.channels || [],
          slug: item.category.slug,
        })) || [];

      setCategories(processedCategories);
    } catch (err) {
      console.error('Error fetching movies data:', err);
      setError("We couldn't load movies right now. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Check if we have preloaded data in cache first
    const checkPreloadedData = async () => {
      try {
        // Check if we have cached data from header preloading
        if (cacheService.hasMoviesPage()) {
          // If we have cached data, try to fetch it immediately
          const data = await spotlightService.getMoviesHomePage();

          // Process homepage data to extract categories with their channels
          const processedCategories =
            data.movies?.map(item => ({
              _id: item.category._id,
              name: item.category.name,
              display_name: item.category.name,
              channels: item.channels || [],
              slug: item.category.slug,
            })) || [];

          setCategories(processedCategories);
          setLoading(false);
          return;
        }
      } catch (err) {
        console.warn(
          'MoviesPage: Failed to use preloaded data, falling back to normal fetch:',
          err,
        );
      }

      // If no preloaded data or it failed, fetch normally
      fetchMoviesData();
    };

    checkPreloadedData();
  }, []);

  // Redirect to homepage if channel query parameter is present
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const channelSlug = searchParams.get('channel');

    if (channelSlug) {
      // Redirect to homepage with the channel parameter
      navigate(`/?channel=${channelSlug}`, { replace: true });
    }
  }, [location.search, navigate]);

  if (error) {
    return (
      <div className="min-h-screen movies-page bg-primary">
        <main className="container px-6 py-8 mx-auto">
          <div className="p-6 text-center border border-red-500 rounded-lg bg-red-900/30">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="w-12 h-12 mx-auto mb-4 text-red-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <h2 className="mb-2 text-xl font-bold text-white">Error Loading Movies</h2>
            <p className="mb-4 text-white/70">{error}</p>
            <div className="flex justify-center space-x-4">
              <button
                onClick={() => navigate('/')}
                className="px-6 py-2 font-medium text-white rounded-full bg-accent hover:bg-accent/80"
              >
                Go Home
              </button>
              <button
                onClick={fetchMoviesData}
                className="px-6 py-2 font-medium text-white rounded-full bg-white/10 hover:bg-white/20"
              >
                Try Again
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen movies-page bg-primary">

      <main className="container px-6 py-8 mx-auto">
        <h1 className="mb-8 text-3xl font-bold text-white">Movies</h1>

        <ErrorBoundary fallback="Failed to load movie categories">
          <Suspense fallback={<LoadingSpinner message="Loading movie categories..." />}>
            {loading ? (
              <LoadingSpinner message="Loading movies..." />
            ) : categories.length > 0 ? (
              categories.map(category => (
                <CategoryRail
                  key={category._id}
                  category={category}
                  onCategorySelect={onCategorySelect}
                  onMovieSelect={onMovieSelect}
                  onSeriesSelect={onSeriesSelect}
                />
              ))
            ) : (
              <div className="py-12 text-center text-white/50">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="w-16 h-16 mx-auto mb-4 text-white/30"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 4v16M17 4v16M3 8h4m10 0h4M3 12h18M3 16h4m10 0h4M4 20h16a1 1 0 001-1V5a1 1 0 00-1-1H4a1 1 0 00-1 1v14a1 1 0 001 1z"
                  />
                </svg>
                <h3 className="mb-2 text-lg font-medium text-white">No Movies Available</h3>
                <p className="text-white/50">Check back later for new movie content.</p>
              </div>
            )}
          </Suspense>
        </ErrorBoundary>
      </main>

    </div>
  );
};

export default MoviesPage;
